import React, { useMemo, useRef } from "react"
import { useSelector } from "react-redux"
import { useEffect, useState } from "react"
import {
  TAAISearchUser,
  TAbanManyUsers,
  TAbanUser,
  TAChatGptSearchUser,
  TAfindAllUser,
  TAfindAllUserCount,
  TAprioritizeUser,
  TAupdateClassification,
  TAverifyUser,
} from "../../../services/userAPI"
import { User } from "../../../types/UserData"
import sortBy from "lodash/sortBy"
import { FilterValue } from "../../../types/FilterDatas"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBan, faSearch, faUserPlus } from "@fortawesome/free-solid-svg-icons"
import { selectGetAllUserFilters } from "../../../redux/store/filters/user/getAllUserSlice"
import EditCategoryModal from "../../../components/EditCategoryModal"
import ProfileDetailModal from "../../../components/ProfileDetailModal"
import { numFixer } from "./components/Functions"
import { columnConfig } from "./components/columns"
import StickyDataTable from "../../../components/StickyDataTable"
import Filters from "./components/Filters"
import { useNavigate } from "react-router-dom"
import { isAbortError, getErrorMessage, getErrorStatus } from "../../../types/error"
import * as XLSX from "xlsx"
import { BrandSectorsData } from "../../../static/Brand"
import InviteUsersModal from "./components/InviteUsersModal"

const GetAllUsers = () => {
  const navigate = useNavigate()
  const userFilters = useSelector(selectGetAllUserFilters)
  const abortControllerRef = useRef<AbortController | null>(null)
  const [page, setPage] = useState(1)
  const PAGE_SIZES = [10, 20, 30, 50, 100, 500, 1000]
  const [pageSize, setPageSize] = useState(PAGE_SIZES[2])
  const [totalRecords, setTotalRecords] = useState(0)
  const [initialRecords, setInitialRecords] = useState<User[]>(sortBy({}, "id"))
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [exportCSV, setExportCSV] = useState("")
  const [category, setCategory] = useState("")
  const [subCategory, setSubCategory] = useState<string[]>([])
  const [selectedRecords, setSelectedRecords] = useState<any>([])
  const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState({
    modal: false,
    _id: "",
  })
  const [selectedUser, setSelectedUser] = useState(null)
  const [AISearch, setAISearch] = useState("")
  const [isOpenDetailModal, setIsOpenDetailModal] = useState<boolean>(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [queryForUser, setQueryForUser] = useState<any>(null)
  const [changePage, setChangePage] = useState(0)
  const [sortByColumn, setSortByColumn] = useState<string>("")
  const [sortOrder, setSortOrder] = useState<boolean>(false)
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)

  useEffect(() => {
    handleUserCounts(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    handleFetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, exportCSV, sortByColumn, sortOrder])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  useEffect(() => {
    if (isEditCategoryModalOpen.modal) {
      const user = initialRecords.find((u) => u._id === isEditCategoryModalOpen._id)
      setCategory(user?.category || "")
      setSubCategory((user?.sub_category as string[]) || [])
    }
  }, [isEditCategoryModalOpen, initialRecords])

  const params = useMemo(() => {
    const flatten = Object.entries(userFilters).reduce((acc, [key, filter]) => {
      const simpleKeys = [
        "gender",
        "verification",
        "warnings",
        "platform",
        "is_spam",
        "unvisible",
        "last_login",
        "frequency",
        "country",
        "city",
        "job",
        "insta_username",
        "tiktok_username",
      ]

      if (simpleKeys.includes(key) && typeof filter === "string") {
        acc[key] = filter
      } else if (filter && typeof filter === "object") {
        const { min, max } = filter as FilterValue
        if (min) {
          acc[`min_${key}`] = min
        }
        if (max) {
          acc[`max_${key}`] = max
        }
      }

      return acc
    }, {} as { [key: string]: string })
    const p = new URLSearchParams(flatten)
    ;(userFilters.keywords || []).forEach((k: any) => p.append("keywords", k))
    ;(userFilters.hobbies || []).forEach((h: any) => p.append("hobbies", h))
    return p
  }, [userFilters])

  const handleFetchData = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setLoading(true)
    if (AISearch.length > 0) {
      handleAISearch(userFilters)
    } else {
      try {
        const response = await TAfindAllUser(
          page,
          pageSize,
          params.toString(),
          sortByColumn,
          sortOrder,
          exportCSV,
          abortControllerRef.current.signal,
        )
        const data = response?.users?.map((item: any) => {
          return {
            insta_fullname: item.insta.full_name,
            insta_username: item.insta.username,
            insta_followers: numFixer(item.insta.followers),
            insta_following: numFixer(item.insta.following),
            insta_average_like: numFixer(item.insta.average_like),
            insta_engagement_rate: numFixer(item.insta.engagement_rate),
            insta_average_reel_view: numFixer(item.insta.average_reel_view),
            insta_face_count: numFixer(item.insta.face_count),
            insta_post_number: numFixer(item.insta.post_number),
            tiktok_username: numFixer(item.tiktok.username),
            tiktok_followers: numFixer(item.tiktok.followers),
            tiktok_following: numFixer(item.tiktok.following),
            tiktok_average_like: numFixer(item.tiktok.tiktok_average_like),
            tiktok_engagement_rate: numFixer(item.tiktok.tiktok_engagement_rate),
            tiktok_hearts: numFixer(item.tiktok.hearts),
            ...item,
          }
        })
        setInitialRecords(data)
        setLoading(false)
      } catch (error: unknown) {
        if (isAbortError(error)) {
          setLoading(false)
          return
        }

        if (getErrorStatus(error) === 401) {
          navigate("/login")
        }
        setLoading(false)
        setError("Error fetch data")
      }
    }
  }

  const handleUserCounts = async (params: any) => {
    try {
      const res = await TAfindAllUserCount(params)
      if (res?.totalDocuments != null) {
        setTotalRecords(res.totalDocuments)
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError("No data found")
    }
  }

  const removeVerification = async (id: any) => {
    try {
      const response = await TAverifyUser(id, "false")
      if (response) {
        setInitialRecords((prevUsers) =>
          prevUsers.map((user) => (user._id === id ? { ...user, verification: false } : user)),
        )
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError("Error remove verification")
    }
  }

  const addVerification = async (id: any) => {
    try {
      const response = await TAverifyUser(id, "true")
      if (response) {
        setInitialRecords((prevUsers) =>
          prevUsers.map((user) => (user._id === id ? { ...user, verification: true } : user)),
        )
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError("Error add verification")
    }
  }

  const banUser = async (id: any) => {
    try {
      const response = await TAbanUser(id)
      if (response) {
        setInitialRecords((prevUsers) => prevUsers.filter((user) => user._id !== id))
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError("Error ban user")
    }
  }

  const handleEditCategory = async (id: any, category: string) => {
    try {
      const response = await TAupdateClassification(id, category, subCategory)
      if (response) {
        setInitialRecords((prevUsers) =>
          prevUsers.map((user) =>
            user._id === id ? { ...user, category, sub_category: subCategory } : user,
          ),
        )
        setCategory("")
        setSubCategory([])
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError("Error edit category")
    }
  }

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      const allIds = initialRecords.map((record) => record._id)
      setSelectedRecords(allIds)
    } else {
      setSelectedRecords([])
    }
  }

  const handleSelectRow = (id: string) => {
    setSelectedRecords((prevSelected: string[]) =>
      prevSelected.includes(id)
        ? prevSelected.filter((selectedId: string) => selectedId !== id)
        : [...prevSelected, id],
    )
  }

  const isAllSelected = selectedRecords.length === initialRecords.length
  const isIndeterminate =
    selectedRecords.length > 0 && selectedRecords.length < initialRecords.length

  const handleBulkDelete = async () => {
    try {
      const response = await TAbanManyUsers(selectedRecords)
      if (response) {
        const updatedRecords = initialRecords.filter(
          (record) => !selectedRecords.includes(record._id),
        )
        setInitialRecords(updatedRecords)
        setSelectedRecords([])
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError("Error bulk delete")
    }
  }

  const handleBulkCreateApplication = () => {
    if (selectedRecords.length === 0) {
      setError("Lütfen en az bir kullanıcı seçin")
      return
    }
    setIsInviteModalOpen(true)
  }

  const getSelectedUsers = () => {
    return initialRecords.filter((user) => selectedRecords.includes(user._id))
  }

  const handleInviteSuccess = () => {
    setSelectedRecords([])
    handleFetchData()
  }

  const handleAISearch = async (userFilters: any) => {
    setLoading(true)
    try {
      const response = await TAChatGptSearchUser(AISearch)
      if (response !== undefined) {
        setQueryForUser(response)
        await handleUserWithQuery(response, userFilters)
      }
      setLoading(false)
    } catch (error: unknown) {
      if (isAbortError(error)) {
        setLoading(false)
        return
      }

      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setLoading(false)
      setError("Error AI search")
    }
  }

  const isValidRange = (range: { min?: string; max?: string }) => {
    const min = range?.min ? Number(range.min) : undefined
    const max = range?.max ? Number(range.max) : undefined

    return (min !== undefined && !isNaN(min)) || (max !== undefined && !isNaN(max))
  }

  const cleanFilters = (filters: Record<string, any>) => {
    return Object.fromEntries(
      Object.entries(filters).filter(([key, value]) => {
        if (value === null || value === "" || (Array.isArray(value) && value.length === 0)) {
          return false
        }

        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          if ("min" in value || "max" in value) {
            return isValidRange(value)
          }
          const cleanedInnerObject = cleanFilters(value)
          return Object.keys(cleanedInnerObject).length > 0
        }

        return true
      }),
    )
  }

  const handleUserWithQuery = async (queryRes: any, userFilters: any) => {
    setLoading(true)

    try {
      const cleanFilterData = cleanFilters(userFilters)
      const query = {
        ...(queryRes || queryForUser),
        isVerification: true,
        ...cleanFilterData,
        ...(cleanFilterData.user_category?.value && {
          user_category: cleanFilterData.user_category.value,
        }),
      }

      const response = await TAAISearchUser(query, page, pageSize, "", true)
      if (response && Array.isArray(response.users)) {
        const totalPages = response.totalPages || 0
        const data = response.users.map((item: any) => ({
          ...item,
        }))

        setInitialRecords(data)
        setTotalRecords(totalPages * pageSize)
      } else {
        setError("No data found.")
      }
    } catch (error: unknown) {
      if (isAbortError(error)) {
        setLoading(false)
        return
      }

      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(getErrorMessage(error) || "An unexpected error occurred.")
    } finally {
      setLoading(false)
    }
  }

  const handlePrioritize = async (id: string, priotory: boolean) => {
    try {
      const response = await TAprioritizeUser(id, priotory)
      if (response) {
        setInitialRecords((prevUsers) =>
          prevUsers.map((user) => (user._id === id ? { ...user, prioritized: priotory } : user)),
        )
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError("Error prioritizing user")
    }
  }

  const prepareDataForExport = (records: User[]) => {
    return records.map((record: any) => {
      const matchedCategory = BrandSectorsData.find((item) => item.value === record.category)
      const categoryName = matchedCategory ? matchedCategory.name : "Kategori Yok"
      const subCategoryText =
        record.sub_category && record.sub_category.length > 0
          ? record.sub_category.join(", ")
          : "Alt Kategori Yok"
      const genderText =
        record.gender === "male" ? "Erkek" : record.gender === "female" ? "Kadın" : ""
      const verificationText = record.verification ? "Onaylı" : "Onaysız"
      const prioritizedText = record.prioritized ? "Öncelikli" : "Önceliksiz"

      // Application stats formatting
      const approvalRate =
        record?.application_stats?.approval_rate != null
          ? Number(record.application_stats.approval_rate).toFixed(2)
          : "-"
      const rejectionRate =
        record?.application_stats?.rejection_rate != null
          ? Number(record.application_stats.rejection_rate).toFixed(2)
          : "-"
      const averageViews =
        record?.application_stats?.averageViews != null
          ? Number(record.application_stats.averageViews).toFixed(2)
          : "-"
      const averageLikes =
        record?.application_stats?.averageLikes != null
          ? Number(record.application_stats.averageLikes).toFixed(2)
          : "-"
      const averageComments =
        record?.application_stats?.averageComments != null
          ? Number(record.application_stats.averageComments).toFixed(2)
          : "-"
      const averageShares =
        record?.application_stats?.averageShares != null
          ? Number(record.application_stats.averageShares).toFixed(2)
          : "-"
      const averageClicks =
        record?.application_stats?.averageClicks != null
          ? Number(record.application_stats.averageClicks).toFixed(2)
          : "-"
      const engagementScore =
        record?.application_stats?.engagement_score?.score != null
          ? Number(record.application_stats.engagement_score.score).toFixed(2)
          : "-"
      const riskScore =
        record?.application_stats?.risk_score?.score != null
          ? Number(record.application_stats.risk_score.score).toFixed(2)
          : "-"
      const isRisingStar = record?.application_stats?.is_rising_star?.score ? "Evet" : "Hayır"
      const uploadingContentDuration = record?.application_stats?.uploading_content_duration ?? "-"
      const uploadingContentScore =
        record?.application_stats?.uploading_content_score?.score != null
          ? Number(record.application_stats.uploading_content_score.score).toFixed(2)
          : "-"

      // Instagram engagement rate formatting
      const instaEngagementRate =
        record.insta_engagement_rate != null ? Number(record.insta_engagement_rate).toFixed(2) : "-"

      // TikTok engagement rate formatting
      const tiktokEngagementRate =
        record.tiktok_engagement_rate != null
          ? Number(record.tiktok_engagement_rate).toFixed(2)
          : "-"

      return {
        Id: record._id || "",
        "Onay Durumu": verificationText,
        "Öncelik Durumu": prioritizedText,
        İsim: record.name || "",
        Telefon: record.phone || "",
        Yaş: record.age > 65 ? "65" : record.age || "",
        Meslek: record.job || "",
        Kategori: categoryName,
        "Alt Kategori": subCategoryText,
        "Insta Tam Adı": record.insta_fullname || "",
        "Insta Kullanıcı Adı": record.insta_username || "",
        "TikTok Kullanıcı Adı": record.tiktok?.username || record.tiktok_username || "",
        Cinsiyet: genderText,
        Ülke: record.country || "",
        "Başvuru Onay Oranı": approvalRate,
        "Başvuru Red Oranı": rejectionRate,
        "Başvuru Ort. İzlenme": averageViews,
        "Başvuru Ort. Beğeni": averageLikes,
        "Başvuru Ort. Yorum": averageComments,
        "Başvuru Ort. Paylaşım": averageShares,
        "Başvuru Ort. Tıklama": averageClicks,
        "Başvuru Etkileşim Skoru": engagementScore,
        "Risk Skoru": riskScore,
        "Yükselen Yıldız": isRisingStar,
        "İçerik Yükleme Süresi": uploadingContentDuration,
        "İçerik Skoru": uploadingContentScore,
        "Insta Takipçi": record.insta_followers || 0,
        "Insta Takip": record.insta_following || 0,
        "Insta Ortalama Beğeni": record.insta_average_like || 0,
        "Insta Ortalama Reel İzlenme": record.insta_average_reel_view || 0,
        "Insta Yüz Sayısı": record.insta_face_count || 0,
        "Insta Etkileşim Oranı": instaEngagementRate,
        "Insta Gönderi Sayısı": record.insta_post_number || 0,
        "TikTok Takipçi": record.tiktok_followers || 0,
        "TikTok Takip": record.tiktok_following || 0,
        "TikTok Ortalama Beğeni": record.tiktok_average_like || 0,
        "TikTok Etkileşim Oranı": tiktokEngagementRate,
        "TikTok Kalp Sayısı": record.tiktok_hearts || 0,
      }
    })
  }

  const convertToCSV = (data: any[]): string => {
    if (!data || data.length === 0) {
      return ""
    }

    const headers = Object.keys(data[0])
    const csvRows = [
      headers.join(","),
      ...data.map((row) =>
        headers
          .map((header) => {
            const value = row[header]
            if (value === null || value === undefined) {
              return ""
            }
            const stringValue = String(value)
            // CSV için özel karakterleri escape et
            if (
              stringValue.includes(",") ||
              stringValue.includes("''") ||
              stringValue.includes("\n")
            ) {
              return `"${stringValue.replace(/"/g, "''")}"`
            }
            return stringValue
          })
          .join(","),
      ),
    ]

    return csvRows.join("\r\n")
  }

  const handleExportCSV = () => {
    if (!initialRecords || initialRecords.length === 0) {
      setError("İndirilecek veri bulunamadı")
      return
    }

    const exportData = prepareDataForExport(initialRecords)
    const csvContent = convertToCSV(exportData)
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const url = window.URL.createObjectURL(blob)

    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", `kullanicilar_${new Date().toISOString().split("T")[0]}.csv`)
    link.style.display = "none"
    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const handleExportExcel = () => {
    if (!initialRecords || initialRecords.length === 0) {
      setError("İndirilecek veri bulunamadı")
      return
    }

    const exportData = prepareDataForExport(initialRecords)
    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Kullanıcılar")
    XLSX.writeFile(workbook, `kullanicilar_${new Date().toISOString().split("T")[0]}.xlsx`)
  }

  const columns = columnConfig({
    isAllSelected,
    isIndeterminate,
    handleSelectAll,
    handleSelectRow,
    selectedRecords,
    removeVerification,
    setSelectedUser,
    setIsOpenDetailModal,
    addVerification,
    banUser,
    setIsEditCategoryModalOpen,
    handlePrioritize,
  })

  const handleSorting = (columnAccessor: string, direction?: "asc" | "desc") => {
    if (columnAccessor === sortByColumn) {
      setSortOrder(direction === "desc")
    } else {
      setSortByColumn(columnAccessor)
      setSortOrder(direction === "desc")
    }
  }

  return (
    <div className="panel">
      <Filters
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        handleFetchData={() => {
          handleFetchData()
          handleUserCounts(params)
        }}
      />
      <div className="flex items-center">
        <div
          className="cursor-pointer inline-flex items-center justify-center p-2 mb-4 border border-transparent text-sm font-medium rounded-3xl text-white bg-gray-400 hover:bg-gray-500"
          onClick={() => handleBulkDelete()}
        >
          <span>Seçilenleri Banla</span>
          <FontAwesomeIcon className="ml-2" icon={faBan} style={{ color: "red" }} />
        </div>
        {selectedRecords.length > 0 && (
          <div
            className="cursor-pointer inline-flex items-center justify-center p-2 mb-4 border border-transparent text-sm font-medium rounded-3xl text-white bg-green-600 hover:bg-green-700 ml-2"
            onClick={handleBulkCreateApplication}
          >
            <span>Seçilenleri Davet Et ({selectedRecords.length})</span>
            <FontAwesomeIcon className="ml-2" icon={faUserPlus} />
          </div>
        )}
        <div
          className="flex w-1/10 bg-gray-100 ml-4 items-center border border-gray-300 rounded-3xl mb-4 p-2 cursor-pointer"
          onClick={() => setIsFilterOpen(true)}
        >
          Filtrele
        </div>
        <div className="flex w-1/3 ml-4 items-center border border-gray-300 rounded-3xl mb-4 bg-gray-100 p-0">
          <FontAwesomeIcon icon={faSearch} className="text-gray-400 ml-2" />
          <input
            type="text"
            placeholder="AI araması yap"
            className="w-full h-8 border-none bg-gray-100 focus:outline-none rounded-3xl pl-3"
            aria-label="AI search"
            value={AISearch}
            onChange={(e) => setAISearch(e.target.value)}
          />
        </div>
        <div className="flex flex-row ml-auto text-center mb-4 mr-2">
          <button
            className=" inline-flex items-center justify-center ml-2 p-2 mt-6 mb-2 border border-transparent text-sm font-medium rounded-3xl text-white bg-blue-600 hover:bg-blue-700"
            onClick={() => handleFetchData()}
          >
            Kullanıcı Ara
          </button>
          <button
            className=" inline-flex items-center justify-center ml-2 px-2 mt-6 mb-2 border border-transparent text-sm font-medium rounded-3xl text-white bg-blue-600 hover:bg-blue-700"
            onClick={() => setExportCSV("true")}
          >
            10 Sayfa Kullanıcı Ara
          </button>
          <button
            className=" inline-flex items-center justify-center ml-2 px-2 mt-6 mb-2 border border-transparent text-sm font-medium rounded-3xl text-white bg-green-600 hover:bg-green-700"
            onClick={handleExportCSV}
          >
            CSV olarak indir
          </button>
          <button
            className=" inline-flex items-center justify-center ml-2 px-2 mt-6 mb-2 border border-transparent text-sm font-medium rounded-3xl text-white bg-green-600 hover:bg-green-700"
            onClick={handleExportExcel}
          >
            Excel olarak indir
          </button>
        </div>
      </div>
      {isEditCategoryModalOpen.modal && (
        <EditCategoryModal
          category={category}
          subCategory={subCategory}
          setCategory={setCategory}
          setSubCategory={setSubCategory}
          onClose={() => {
            setIsEditCategoryModalOpen({ modal: false, _id: "" })
            setCategory("")
            setSubCategory([])
          }}
          handleSave={() => handleEditCategory(isEditCategoryModalOpen._id, category)}
        />
      )}
      <ProfileDetailModal
        data={selectedUser}
        isOpen={isOpenDetailModal}
        onClose={() => {
          setIsOpenDetailModal(false)
        }}
      />
      <InviteUsersModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        selectedUsers={getSelectedUsers()}
      />
      <div className="datatables">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
          </div>
        ) : (
          <div className="flex flex-col w-full">
            <StickyDataTable
              highlightOnHover={true}
              className="whitespace-nowrap"
              records={initialRecords}
              columns={columns}
              totalRecords={totalRecords}
              recordsPerPage={pageSize}
              page={page}
              onPageChange={(p) => setPage(p)}
              recordsPerPageOptions={PAGE_SIZES}
              onRecordsPerPageChange={setPageSize}
              sortStatus={{
                columnAccessor: sortByColumn,
                direction: sortOrder ? "desc" : "asc",
              }}
              onSortStatusChange={(e) => handleSorting(e.columnAccessor, e.direction)}
              minHeight={200}
              paginationText={({ from, to, totalRecords }) =>
                `Showing  ${from} to ${to} of ${totalRecords} entries`
              }
            />
            <div className="flex justify-end items-center gap-3 mt-4 mr-10">
              <input
                type="number"
                placeholder="Sayfa No"
                value={changePage}
                className="w-20 px-2 py-2 text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                onChange={(e) => setChangePage(e.target.valueAsNumber)}
                min={1}
                max={Math.ceil(totalRecords / pageSize)}
              />

              <button
                className="px-4 py-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition"
                onClick={() => setPage(changePage)}
              >
                Atla
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default GetAllUsers
